import React, { useEffect, useState } from "react";
import api, { formatDate, formatDateTime, formatErrorDetail } from "@/lib/api";
import { PageHeader, Surface, TableWrap, Empty } from "@/components/app/Primitives";
import EmployeePicker from "@/components/app/EmployeePicker";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Plus, LogOut, Copy, Rocket, Mail, ExternalLink, PenLine } from "lucide-react";

const STATUS_BADGE = {
    scheduled: "badge badge-draft", clearance_in_progress: "badge badge-pending",
    awaiting_finance: "badge badge-info", cleared: "badge badge-approved", cancelled: "badge badge-destructive",
};

export default function Offboarding() {
    const [data, setData] = useState(null);
    const [open, setOpen] = useState(false);
    const [form, setForm] = useState({ employee_id: "", last_day: "", reason: "resignation" });
    const [signers, setSigners] = useState([]);
    const [sim, setSim] = useState(null);
    const [editSigners, setEditSigners] = useState(null); // offboarding being edited

    const load = () => api.get("/offboarding").then((r) => setData(r.data)).catch(() => {});
    useEffect(() => { load(); }, []);

    const openCreate = async () => {
        try { const { data: d } = await api.get("/offboarding/default-signers"); setSigners(d.signers); } catch { setSigners([]); }
        setOpen(true);
    };

    const create = async () => {
        if (!form.employee_id || !form.last_day) return toast.error("Pick the employee and their last day");
        try {
            await api.post("/offboarding", { ...form, signers });
            toast.success("Offboarding scheduled — clearance launches automatically on the last day");
            setOpen(false); setForm({ employee_id: "", last_day: "", reason: "resignation" });
            load();
        } catch (e) { toast.error(formatErrorDetail(e.response?.data?.detail)); }
    };

    const launch = async (ob) => {
        if (!window.confirm(`Start the clearance for ${ob.employee_name} NOW? Department heads will be emailed their sign links.`)) return;
        try { await api.post(`/offboarding/${ob.id}/launch`); toast.success("Clearance launched — sign links emailed"); load(); }
        catch (e) { toast.error(formatErrorDetail(e.response?.data?.detail)); }
    };

    const openSim = async (ob) => {
        try { const { data: d } = await api.get(`/offboarding/${ob.id}/email-previews`); setSim({ ...d, ob }); }
        catch (e) { toast.error(formatErrorDetail(e.response?.data?.detail)); }
    };

    const copyLink = (ob) => {
        navigator.clipboard?.writeText(`${window.location.origin}/clearance/${ob.token}`);
        toast.success("Special monitoring link copied — share with HR and the employee");
    };

    const saveSigners = async () => {
        try {
            await api.put(`/offboarding/${editSigners.id}/signers`, { signers: editSigners.signers });
            toast.success("Signers updated");
            setEditSigners(null); load();
        } catch (e) { toast.error(formatErrorDetail(e.response?.data?.detail)); }
    };

    const SignerEditor = ({ rows, onChange }) => (
        <div className="space-y-1.5 max-h-56 overflow-auto">
            {rows.map((s, i) => (
                <div key={s.id || i} className="grid grid-cols-[110px_1fr_1fr] gap-1.5 items-center">
                    <span className="text-xs font-medium truncate">{s.order}. {s.department}{s.is_finance && <span className="text-[9px] text-primary font-bold block">FINAL</span>}</span>
                    <Input className="h-8 text-xs" value={s.signer_name} placeholder="Signer name" onChange={(e) => onChange(i, "signer_name", e.target.value)} />
                    <Input className="h-8 text-xs" value={s.signer_email || ""} placeholder="Email" onChange={(e) => onChange(i, "signer_email", e.target.value)} />
                </div>
            ))}
        </div>
    );

    return (
        <div>
            <PageHeader title="Offboarding & Clearance" subtitle="Tag an employee's last day — on that day, every department head gets an e-sign clearance email. Finance signs last. HR and the employee monitor through the special link."
                actions={
                    <button className="h-9 px-3 rounded-md bg-primary text-white text-sm font-medium flex items-center gap-2" onClick={openCreate} data-testid="new-offboarding-btn">
                        <Plus className="size-4" /> Tag employee for offboarding
                    </button>
                } />

            {data && !data.items.length && <Surface className="p-6"><Empty title="No offboardings" subtitle="Tag an employee to schedule their clearance." /></Surface>}
            {data?.items.length > 0 && (
                <TableWrap>
                    <thead><tr><th>Employee</th><th>Last day</th><th>Reason</th><th>Status</th><th>Clearance</th><th>Actions</th></tr></thead>
                    <tbody>
                        {data.items.map((ob) => (
                            <tr key={ob.id} data-testid={`ob-${ob.id}`}>
                                <td><b>{ob.employee_name}</b> <span className="text-xs text-muted-foreground">{ob.employee_no} · {ob.position || ""}</span></td>
                                <td className="font-mono text-xs">{ob.last_day}</td>
                                <td className="text-xs capitalize">{ob.reason}</td>
                                <td><span className={STATUS_BADGE[ob.status] || "badge"}>{ob.status.replace(/_/g, " ")}</span></td>
                                <td>
                                    <div className="flex items-center gap-2">
                                        <div className="w-20 h-1.5 rounded-full bg-muted overflow-hidden"><div className={`h-full ${ob.status === "cleared" ? "bg-emerald-500" : "bg-primary"}`} style={{ width: `${ob.signer_count ? (ob.signed_count / ob.signer_count) * 100 : 0}%` }} /></div>
                                        <span className="text-xs font-mono">{ob.signed_count}/{ob.signer_count}</span>
                                    </div>
                                </td>
                                <td>
                                    <div className="flex items-center gap-1.5 flex-wrap">
                                        {ob.status === "scheduled" && <button className="h-7 px-2 text-xs rounded-md bg-primary text-white flex items-center gap-1" onClick={() => launch(ob)} data-testid={`launch-${ob.id}`}><Rocket className="size-3" /> Launch now</button>}
                                        {ob.status === "scheduled" && <button className="h-7 px-2 text-xs rounded-md border border-border flex items-center gap-1" onClick={() => setEditSigners(JSON.parse(JSON.stringify(ob)))}><PenLine className="size-3" /> Signers</button>}
                                        <button className="h-7 px-2 text-xs rounded-md border border-border flex items-center gap-1" onClick={() => openSim(ob)} data-testid={`sim-${ob.id}`}><Mail className="size-3" /> Email simulator</button>
                                        <a className="h-7 px-2 text-xs rounded-md border border-primary/50 text-primary flex items-center gap-1" href={`/clearance/${ob.token}`} target="_blank" rel="noreferrer" data-testid={`portal-${ob.id}`}><ExternalLink className="size-3" /> Clearance page</a>
                                        <button className="h-7 px-2 text-xs rounded-md border border-border flex items-center gap-1" onClick={() => copyLink(ob)}><Copy className="size-3" /> Copy link</button>
                                    </div>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </TableWrap>
            )}

            {/* Tag dialog */}
            <Dialog open={open} onOpenChange={setOpen}>
                <DialogContent className="max-w-lg">
                    <DialogHeader><DialogTitle className="flex items-center gap-2"><LogOut className="size-4 text-primary" /> Tag employee for offboarding</DialogTitle></DialogHeader>
                    <div className="space-y-2">
                        <div><Label>Employee</Label><EmployeePicker value={form.employee_id} onChange={(v) => setForm({ ...form, employee_id: v })} testId="ob-employee" /></div>
                        <div className="grid grid-cols-2 gap-2">
                            <div><Label>Last working day</Label><Input type="date" value={form.last_day} onChange={(e) => setForm({ ...form, last_day: e.target.value })} data-testid="ob-lastday" /></div>
                            <div>
                                <Label>Reason</Label>
                                <select className="h-9 w-full rounded-md border border-border bg-background px-2 text-sm" value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })}>
                                    {["resignation", "end of contract", "retirement", "redundancy", "termination"].map((r) => <option key={r} value={r}>{r}</option>)}
                                </select>
                            </div>
                        </div>
                        <div>
                            <Label>Clearance signatories (Finance always signs last)</Label>
                            <SignerEditor rows={signers} onChange={(i, k, v) => setSigners(signers.map((s, j) => j === i ? { ...s, [k]: v } : s))} />
                        </div>
                        <div className="text-xs text-muted-foreground">On the last day the clearance launches automatically: each head gets an e-sign email; HR and the employee get the monitoring link.</div>
                    </div>
                    <DialogFooter><button className="h-9 px-3 rounded-md bg-primary text-white text-sm" onClick={create} data-testid="ob-save">Schedule offboarding</button></DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Edit signers */}
            <Dialog open={!!editSigners} onOpenChange={(v) => !v && setEditSigners(null)}>
                <DialogContent className="max-w-lg">
                    <DialogHeader><DialogTitle>Clearance signatories — {editSigners?.employee_name}</DialogTitle></DialogHeader>
                    {editSigners && <SignerEditor rows={editSigners.signers} onChange={(i, k, v) => setEditSigners({ ...editSigners, signers: editSigners.signers.map((s, j) => j === i ? { ...s, [k]: v } : s) })} />}
                    <DialogFooter><button className="h-9 px-3 rounded-md bg-primary text-white text-sm" onClick={saveSigners}>Save</button></DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Email simulator */}
            <Dialog open={!!sim} onOpenChange={(v) => !v && setSim(null)}>
                <DialogContent className="max-w-2xl">
                    <DialogHeader><DialogTitle>📧 Clearance Email Simulator — {sim?.employee}</DialogTitle></DialogHeader>
                    {sim && (
                        <div className="space-y-2 max-h-[70vh] overflow-auto pr-1">
                            <div className="text-xs text-muted-foreground">
                                Special monitoring link (HR + employee): <a className="text-primary underline break-all" href={sim.monitor_link} target="_blank" rel="noreferrer">{sim.monitor_link}</a>
                                <div className="mt-1">These are the exact emails sent on the last day ({sim.last_day}); the Finance email only goes out after every other department signs.</div>
                            </div>
                            {sim.emails.map((e, i) => (
                                <div key={i} className={`rounded-md border p-3 ${e.kind === "finance_sign" ? "border-blue-300 bg-blue-50/50" : e.kind === "completed" ? "border-emerald-300 bg-emerald-50/50" : "border-border"}`} data-testid={`obsim-${e.kind}-${i}`}>
                                    <div className="flex items-center justify-between flex-wrap gap-1 mb-1">
                                        <span className="badge badge-draft uppercase text-[9px]">{e.kind.replace(/_/g, " ")}</span>
                                        <span className="text-[10px] text-muted-foreground">to: {e.to}</span>
                                    </div>
                                    <div className="text-sm font-semibold">{e.subject}</div>
                                    <pre className="text-xs text-muted-foreground whitespace-pre-wrap mt-1 font-sans">{e.body}</pre>
                                </div>
                            ))}
                        </div>
                    )}
                </DialogContent>
            </Dialog>
        </div>
    );
}
